/**
 * TELLS THE SELECTED VERIFIERS AND APPROVERS THAT A CORRECTION IS WAITING FOR THEM (desk, 2026-10-09: "both verifier and approval those
 * two get notification").
 *
 * Until now they only saw a count (the header chips and the sidebar badge), and only after the page happened to refresh. Verifying
 * a request wrote the verifier's name on it and told nobody. This hook watches the same `GET /financial/correction-workflow/me`
 * answer the header already uses — now asked every minute for a selected user (`useCorrectionWorkflowMe`) — and, when the number waiting for
 * THIS person goes up, raises an unread entry in the bell plus a corner toast that says what is waiting and how to get there:
 *   - a verifier is told when a request has been entered (it needs verifying);
 *   - an approver is told when a request has been verified (it needs approving).
 * Nothing else is sent, and nothing leaves the browser: this is an in-app alert, so it reaches someone whose app is open (on a phone: installed
 * and running, or a tab that is open) - it cannot wake a closed app. SMS and WhatsApp are not connected on this system (both providers are `stub`).
 *
 * WHAT COUNTS AS "NEW": the count is remembered per person in `sessionStorage` (so a reload or a new tab of the same session does not repeat itself).
 * Going up announces the whole current count; staying level or going down announces nothing and just lowers the memory, so the next request
 * is announced again. A person who signs in with 3 waiting is told once, which is the reminder they need.
 */
import { useEffect } from 'react';
import type { CorrectionWorkflowMeDto } from '@nexao/contracts';
import { notify } from '@core/notifications/notify';
import { useAuthStore } from '@store/auth.store';
import { useCorrectionWorkflowMe } from './useCorrectionWorkflow';

export type CorrectionAlertStage = 'verify' | 'approve';
export interface CorrectionAlertMemory {
  verify: number;
  approve: number;
}
export interface CorrectionAlert {
  stage: CorrectionAlertStage;
  count: number;
}

/** What waits for THIS person at each stage — never what they entered or verified themselves (the server already leaves those out). */
export function waitingCounts(me: Pick<CorrectionWorkflowMeDto, 'isVerifier' | 'isApprover' | 'awaitingMyVerification' | 'awaitingMyApproval'> | undefined): CorrectionAlertMemory {
  return {
    verify: me?.isVerifier ? me.awaitingMyVerification : 0,
    approve: me?.isApprover ? me.awaitingMyApproval : 0,
  };
}

/** Pure: which alerts to raise now, and what to remember afterwards. */
export function planCorrectionAlerts(me: Parameters<typeof waitingCounts>[0], memory: CorrectionAlertMemory): { alerts: CorrectionAlert[]; memory: CorrectionAlertMemory } {
  const now = waitingCounts(me);
  const alerts: CorrectionAlert[] = [];
  if (now.verify > memory.verify) alerts.push({ stage: 'verify', count: now.verify });
  if (now.approve > memory.approve) alerts.push({ stage: 'approve', count: now.approve });
  return { alerts, memory: now };
}

export const CORRECTION_ALERT_TEXT: Record<CorrectionAlertStage, { title: string; href: string; many: (n: number) => string; one: string }> = {
  verify: {
    title: 'Correction to verify',
    href: '/corrections',
    one: '1 correction request is waiting for you to verify.',
    many: (n) => `${n} correction requests are waiting for you to verify.`,
  },
  approve: {
    title: 'Correction to approve',
    href: '/modifications/correction',
    one: '1 verified correction is waiting for your approval.',
    many: (n) => `${n} verified corrections are waiting for your approval.`,
  },
};

const memoryKey = (userId: string): string => `nexao:correction-alerts:${userId}`;

function readMemory(userId: string): CorrectionAlertMemory {
  try {
    const parsed = JSON.parse(sessionStorage.getItem(memoryKey(userId)) ?? 'null') as Partial<CorrectionAlertMemory> | null;
    return { verify: Number(parsed?.verify) || 0, approve: Number(parsed?.approve) || 0 };
  } catch {
    return { verify: 0, approve: 0 };
  }
}

function writeMemory(userId: string, memory: CorrectionAlertMemory): void {
  try {
    sessionStorage.setItem(memoryKey(userId), JSON.stringify(memory));
  } catch {
    /* storage can be blocked (private window): the alert then repeats on the next reload, which is harmless */
  }
}

export function useCorrectionAlerts(): void {
  const me = useCorrectionWorkflowMe();
  const userId = useAuthStore((state) => state.user?.id);
  const data = me.data;

  useEffect(() => {
    if (!userId || !data) return;
    const { alerts, memory } = planCorrectionAlerts(data, readMemory(userId));
    writeMemory(userId, memory);
    for (const alert of alerts) {
      const text = CORRECTION_ALERT_TEXT[alert.stage];
      notify.info({ title: text.title, message: alert.count === 1 ? text.one : text.many(alert.count), href: text.href, keepUnread: true });
    }
  }, [data, userId]);
}
